import { useState, useEffect, useCallback } from "react";
import { clusterApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Hook that fetches the first available client/domain for the current tenant.
 * Falls back to "default" if no clients are found or the user is not authenticated.
 */
export function useFirstClient() {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [firstClientId, setFirstClientId] = useState<string>("default");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchFirstClient = useCallback(async () => {
    if (isAuthLoading) {
      setIsLoading(true);
      return;
    }
    if (!isAuthenticated) {
      setFirstClientId("default");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const clients = await clusterApi.getClients();
      if (clients && clients.length > 0) {
        setFirstClientId(clients[0].id);
      } else {
        setFirstClientId("default");
      }
    } catch (error) {
      console.error("Failed to fetch clients for redirect:", error);
      setFirstClientId("default");
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    fetchFirstClient();
  }, [fetchFirstClient]);

  return { firstClientId, isLoading, refetch: fetchFirstClient };
}
